import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import cn from "classnames";
import Button from "../../../../../components/Buttons/button";
import Pill from "../../../../Pill/pill";
import BasicModal from "../../../../models/models";
import SearchIcon from "../../../../../assets/images/search.png";
import SortIcon from "../../../../../assets/images/arrow-down-circle.svg";
import MenuDotIcon from "../../../../../assets/images/menuDot.png";
import MenuDropdown from "../../../../Dropdown/menu-dropdown";
import {
  getAll_employees,
  setUserMenuUpdatedFalse,
  resetPasswordEmail,
  selectCompanyDashboard,
} from "../../../../../redux/slices/companyDashboardSlice";
import { getUserDetails, selectUser } from "../../../../../redux/slices/userSlice";
import { getInitials } from "../../../../../shared/lib/getInitials";
import { getColorHex } from "../../../../../shared/lib/getColorHex";

// Helper function to parse date strings (DD/MM/YYYY)
const parseDate = (dateString) => {
  const [day, month, year] = dateString.split("/");
  return new Date(`${year}-${month}-${day}`);
};

// Sub-component for table header with sorting
const TableHeader = ({ title, onSort, sortOrder, widthClass = "" }) => (
  <th className={`text-left text-secondary border-[#434A5D] py-[12px] px-[16px] ${widthClass}`}>
    <Button
      className="sorting_btn_wrapper"
      secondaryHoverOutlined={true}
      outlined={true}
      onClick={onSort}
    >
      <span className="flex items-center text-[#A5AEBC]">
        {title}
        <img
          className={cn("sortingicon", { "rotate-180": sortOrder === "desc" })}
          src={SortIcon}
          alt="icon"
        />
      </span>
    </Button>
  </th>
);

const ManageEmployees = ({ setModel }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { allEmployees, serviceUpdated, userMenuUpdated } = useSelector(selectCompanyDashboard);
  const { userDetails } = useSelector(selectUser);
  const [popup, setPopup] = useState(false);
  const [state, setState] = useState(0);
  const [empID, setEmpID] = useState(0);
  const [userRole, setUserRole] = useState("");
  const [empReport, setEmpReport] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [sortedUsers, setSortedUsers] = useState([]);
  const [sortOrder, setSortOrder] = useState("asc");

  const employees = allEmployees?.all_employee;
  const isAdmin = userDetails?.user?.role === "admin";

  // Fetch employees on mount and when service or user menu updates
  useEffect(() => {
    dispatch(getAll_employees());
    dispatch(getUserDetails());
  }, [dispatch, serviceUpdated, userMenuUpdated]);

  // Update sorted users when employees change
  useEffect(() => {
    if (employees) {
      setSortedUsers([...employees]);
    }
  }, [employees]);

  // Filter users based on search term
  const displayUsers = useCallback(() => {
    const searchTermLower = searchTerm.toLowerCase();
    return sortedUsers.filter((user) =>
      [user.name, user.email, user.id?.toString()].some(
        (field) => field?.toLowerCase().includes(searchTermLower)
      )
    );
  }, [searchTerm, sortedUsers])();

  // Generic sort handler
  const handleSort = useCallback((key, compareFn) => {
    const sorted = [...sortedUsers].sort((a, b) =>
      sortOrder === "asc" ? compareFn(a, b) : compareFn(b, a)
    );
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    setSortedUsers(sorted);
  }, [sortOrder, sortedUsers]);

  // Specific sort functions
  const sortByName = () => handleSort("name", (a, b) => a.name.localeCompare(b.name));
  const sortByRole = () => handleSort("role", (a, b) => a.role.localeCompare(b.role));
  const sortByAccess = () => handleSort("user_access", (a, b) =>
    a.user_access === b.user_access ? 0 : a.user_access ? 1 : -1
  );
  const sortByAdded = () => handleSort("added", (a, b) =>
    parseDate(a.added) - parseDate(b.added)
  );

  const handleMenuClick = useCallback((userId, role, report) => {
    setUserRole(role);
    setEmpID(userId);
    setEmpReport(report);
  }, []);

  const toggleDropdown = useCallback((index) => {
    setDropdownOpen((prevOpenDropdown) =>
      prevOpenDropdown === index ? null : index
    );
  }, []);

  const viewEmployeeReport = useCallback((userId) => {
    window.open(`/company_dashboard/employees/employee_report/${userId}`);
  }, []);

  const getMenuItems = useCallback((user, index) => [
    {
      label: "Change employee role",
      onClick: () => {
        handleMenuClick(user.id, user.role, user.user_access);
        toggleDropdown(index);
        setState(46);
        setPopup(true);
        dispatch(setUserMenuUpdatedFalse());
      },
    },
    {
      label: user.user_access ? "Deactivate employee" : "Activate employee",
      onClick: () => {
        handleMenuClick(user.id, user.role, user.user_access);
        toggleDropdown(index);
        setState(48);
        setPopup(true);
        dispatch(setUserMenuUpdatedFalse());
      },
    },
    {
      label: "View employee report",
      onClick: () => {
        toggleDropdown(index);
        setModel(false);
        navigate(`/company_dashboard/employees/employee_report/${user.id}`); // Matches original menu nav
      },
    },
    {
      label: "Reset password",
      onClick: () => {
        dispatch(resetPasswordEmail(user.id));
      },
    },
    {
      label: "Remove",
      onClick: () => {
        handleMenuClick(user.id, user.role, user.user_access);
        toggleDropdown(index);
        setState(47);
        setPopup(true);
        dispatch(setUserMenuUpdatedFalse());
      },
    },
  ], [handleMenuClick, toggleDropdown, setModel, navigate, dispatch]);

  return (
    <div className="px-3 md:px-4 pt-3 md:pt-6 ManageServicesBox">
      <div className="headingBox flex justify-between items-start md:items-center flex-col md:flex-row mb-3 md:mb-6 px-0 md:!px-4 gap-2 md:gap-0">
        <h2 className="md:text-2xl font-bold">Manage Employees</h2>
        <div className="flex flex-row gap-[12px] md:gap-[24px]">
          <div className="flex flex-col items-start md:items-end">
            <span className="text-secondary">integration data:</span>
            <span className="text-secondary !italic">
              integration name(status)
            </span>
          </div>
          {isAdmin && (
            <Button
              onClick={() => {
                setPopup(true);
                setState(49);
              }}
              outlined={true}
              hoverColor={true}
              secondary={true}
              secondaryHoverOutlined={true}
            >
              Update Employees
            </Button>
          )}
        </div>
      </div>

      <div className="searchBox md:!mx-4">
        <img src={SearchIcon} alt="icon" className="searchIcon" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, email, or employee ID"
          className="searchInput"
        />
      </div>

      <div className="rounded-t-xl !mt-0 w-[100%] scrollbar-hide scrollable_data">
        <div className="overflow-x-auto scrollbar-hide">
          <table className="sticky top-0">
            <thead className="table-head sticky top-0 bg-[#363D50]">
              <tr>
                <TableHeader title="Name" onSort={sortByName} sortOrder={sortOrder} widthClass="w-[400px]" />
                <TableHeader title="Role" onSort={sortByRole} sortOrder={sortOrder} />
                <TableHeader title="User Access" onSort={sortByAccess} sortOrder={sortOrder} />
                <TableHeader title="Added" onSort={sortByAdded} sortOrder={sortOrder} />
                <th className="text-left text-secondary border-[#434A5D] py-[12px] px-[12px]">
                  &nbsp;
                </th>
              </tr>
            </thead>
            <tbody>
              {displayUsers?.map((user, index) => {
                const [hexColor, hexWithOpacity] = getColorHex(user.id);
                return (
                  <tr
                    key={user.id}
                    className="hover:bg-[#41485C]"
                    onClick={() => viewEmployeeReport(user.id)}
                  >
                    <td className="text-left text-secondary !text-[#FFFFFF] py-[13.5px] px-[16px]">
                      <div className="flex flex-row items-center gap-[10px]">
                        <div
                          style={{
                            backgroundColor: hexWithOpacity,
                            color: hexColor,
                          }}
                          className="w-[40px] h-[40px] rounded-full flex items-center justify-center"
                        >
                          {getInitials(user.name)}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-secondary lineHeight !text-[#FFFFFF] !text-left !font-medium">
                            {user.name}
                          </span>
                          <span className="text-secondary lineHeight !text-left">
                            {user.distinction}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="text-left text-secondary !text-[#FFFFFF] py-[13.5px] px-[28px] !font-medium font_12px">
                      {user.role}
                    </td>
                    <td className="text-left text-secondary !text-[#FFFFFF] py-[13.5px] px-[28px] !font-medium font_12px">
                      {user.user_access === true ? (
                        <Pill greenFill={true}> Active </Pill>
                      ) : (
                        <Pill redFill={true}>Deactivated </Pill>
                      )}
                    </td>
                    <td className="text-left lineHeight text-secondary !text-[#FFFFFF] py-[13.5px] px-[28px] !font-medium">
                      {user?.added}
                    </td>
                    <td className="text-left text-secondary !text-[#FFFFFF] py-[13.5px] px-[28px] !font-medium">
                      <MenuDropdown
                        isOpen={dropdownOpen === index}
                        toggleDropdown={() => toggleDropdown(index)}
                        menuItems={getMenuItems(user, index)}
                        menuIconSrc={MenuDotIcon}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {popup && (
        <BasicModal
          state={state}
          popup={popup}
          setPopup={setPopup}
          setState={setState}
          empID={empID}
          userRole={userRole}
          setModel={setModel}
          empReport={empReport}
        />
      )}
    </div>
  );
};

export default ManageEmployees;
